import { useEffect, useState, useCallback, useRef } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, Check, AlertTriangle, ImageIcon } from "lucide-react";

// items: [{ id, name }], entity: "products" | "equipment"
export default function BulkImageFinder({ open, onOpenChange, items, entity, onDone }) {
  const [state, setState] = useState({});
  const [globalError, setGlobalError] = useState(null);
  const blockedRef = useRef(false);

  const searchOne = useCallback(async (item) => {
    setState((s) => ({ ...s, [item.id]: { ...s[item.id], loading: true, error: null } }));
    try {
      const { data } = await api.get("/products/image-search", { params: { q: item.name } });
      setState((s) => ({ ...s, [item.id]: { loading: false, results: data.results || [], error: null, chosen: s[item.id]?.chosen || null } }));
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail);
      setState((s) => ({ ...s, [item.id]: { loading: false, results: [], error: msg } }));
      if (e.response?.status === 400 && /restricted|not configured|Custom Search/i.test(msg)) { setGlobalError(msg); blockedRef.current = true; }
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setState({});
    setGlobalError(null);
    blockedRef.current = false;
    (async () => {
      for (const it of items) {
        if (blockedRef.current) { setState((s) => ({ ...s, [it.id]: { loading: false, results: [], error: "Google image search is unavailable (see message above)." } })); continue; }
        // eslint-disable-next-line no-await-in-loop
        await searchOne(it);
      }
    })();
  }, [open, items, searchOne]);

  const choose = async (item, url) => {
    setState((s) => ({ ...s, [item.id]: { ...s[item.id], saving: true } }));
    try {
      await api.post(`/${entity}/${item.id}/import-image`, { url });
      setState((s) => ({ ...s, [item.id]: { ...s[item.id], saving: false, chosen: url } }));
      toast.success(`Image set for ${item.name}`);
      onDone?.();
    } catch (e) {
      setState((s) => ({ ...s, [item.id]: { ...s[item.id], saving: false } }));
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto rounded-none sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight">Find images for {items.length} {entity === "products" ? "product(s)" : "item(s)"}</DialogTitle>
          <DialogDescription>Pick the right image for each. Google image results appear below each name.</DialogDescription>
        </DialogHeader>

        {globalError && (
          <div className="flex items-start gap-2 border border-[#b32233]/30 bg-[#fdf0f1] p-3 text-sm text-[#b32233]" data-testid="bulk-image-global-error">
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <span>{globalError}</span>
          </div>
        )}

        <div className="space-y-4">
          {items.map((it) => {
            const s = state[it.id] || {};
            return (
              <div key={it.id} className="border border-zinc-200 p-3" data-testid={`bulk-image-item-${it.id}`}>
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-sm font-medium text-zinc-900">{it.name}</span>
                  {s.chosen && <span className="flex items-center gap-1 text-xs font-medium text-green-600"><Check className="h-3.5 w-3.5" /> Image set</span>}
                </div>
                {s.loading ? (
                  <div className="flex h-20 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-[#b32233]" /></div>
                ) : s.error ? (
                  <p className="flex items-center gap-1 text-xs text-zinc-500"><AlertTriangle className="h-3.5 w-3.5 text-[#b32233]" /> {s.error}</p>
                ) : (s.results || []).length === 0 ? (
                  <p className="flex items-center gap-1 text-xs text-zinc-400"><ImageIcon className="h-3.5 w-3.5" /> No images found</p>
                ) : (
                  <div className="grid grid-cols-6 gap-2">
                    {s.results.map((r, i) => (
                      <button key={i} onClick={() => choose(it, r.image_url)} disabled={s.saving} data-testid={`bulk-image-${it.id}-${i}`} title={r.title}
                        className={`group relative aspect-square overflow-hidden border bg-white transition-colors disabled:opacity-60 ${s.chosen === r.image_url ? "border-[#b32233] ring-2 ring-[#b32233]" : "border-zinc-200 hover:border-[#b32233]"}`}>
                        <img src={r.thumbnail_url} alt={r.title} className="h-full w-full object-cover" />
                        {s.chosen === r.image_url && <span className="absolute inset-0 flex items-center justify-center bg-[#b32233]/30"><Check className="h-5 w-5 text-white" /></span>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
